import './FameRecognitionPanel.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.4 (2026-06-06) — FameRecognitionPanel.
 *
 * Pure-presentation render of a `force_fame_recognition` dispatch action.
 * Canonical Ch 8 fame-recognition chart roll (4-tier × 4-QR → N/?/Y).
 * DISTINCT from `force_recognize_npc` which is the GM override toggle —
 * this surfaces the canonical roll setup + (optionally) the resolved
 * recognition outcome.
 *
 * Schema 1-to-1 with `force_fame_recognition` tool args in
 * briefing_room_backend/routes/dispatch.py:555-588:
 *   - target_npc_id (string, required)
 *   - disguise_qr (integer 1-4 | null): QR of target's active disguise.
 *     null/omitted = no disguise.
 *   - rationale: GM voice-line ≤30 words (optional).
 *
 * Disguise EF modifier per seed/tables/fame_disguise_modifiers.json:
 *   QR 1 (Excellent) → -5 EF, QR 2 (Very Good) → -3, QR 3 (Good) → -1,
 *   QR 4 (Failed disguise) → +2 EF (easier recognition).
 * Base Perception EF = 5; effective EF = clamp(5 + modifier, 0, 10).
 */

export type FameOutcomeCell = 'N' | '?' | 'Y';

export interface FameRecognitionPanelProps {
  targetNpcId: string;
  targetNpcDisplay?: string;
  /** Disguise QR 1-4 if target has an active disguise. null/undefined = no disguise. */
  disguiseQr?: number | null;
  rationale?: string;
  /** Resolved recognition cell. Omit/null → pending state. */
  outcome?: FameOutcomeCell | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const BASE_PERCEPTION_EF = 5;

const DISGUISE_EF_MODIFIER: Record<1 | 2 | 3 | 4, number> = {
  1: -5,
  2: -3,
  3: -1,
  4: 2,
};

const DISGUISE_QR_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: 'Excellent (QR 1)',
  2: 'Very Good (QR 2)',
  3: 'Good (QR 3)',
  4: 'Failed disguise',
};

const OUTCOME_LABEL: Record<FameOutcomeCell, string> = {
  N: 'Unrecognized',
  '?': 'Familiar — maybe',
  Y: 'Identified',
};

function defaultDisplayName(id: string): string {
  return id
    .split('_')
    .map((part) => part.length === 0 ? part : part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

function isValidDisguiseQr(qr: number | null | undefined): qr is 1 | 2 | 3 | 4 {
  return qr === 1 || qr === 2 || qr === 3 || qr === 4;
}

export function FameRecognitionPanel({
  targetNpcId,
  targetNpcDisplay,
  disguiseQr,
  rationale,
  outcome,
  onDismiss,
  testidPrefix = 'br-fame-panel',
}: FameRecognitionPanelProps) {
  const display = targetNpcDisplay ?? defaultDisplayName(targetNpcId);
  const hasDisguise = isValidDisguiseQr(disguiseQr);
  const modifier = hasDisguise ? DISGUISE_EF_MODIFIER[disguiseQr] : 0;
  const effectiveEf = Math.max(0, Math.min(10, BASE_PERCEPTION_EF + modifier));
  const hasOutcome = outcome === 'N' || outcome === '?' || outcome === 'Y';

  return (
    <aside
      className="br-fame-panel"
      data-testid={testidPrefix}
      data-target-npc-id={targetNpcId}
      data-has-disguise={hasDisguise ? 'true' : 'false'}
      data-outcome={hasOutcome ? outcome : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-fame-panel-header">
        <span className="br-fame-panel-label">Recognition</span>
        <span
          className="br-fame-panel-target"
          data-testid={`${testidPrefix}-target`}
        >
          {display}
        </span>
      </header>

      <dl
        className="br-fame-panel-ef"
        data-testid={`${testidPrefix}-ef`}
      >
        <div className="br-fame-panel-ef-row">
          <dt>Base Perception EF</dt>
          <dd
            className="br-mono"
            data-testid={`${testidPrefix}-base-ef`}
          >
            {BASE_PERCEPTION_EF}
          </dd>
        </div>
        {hasDisguise && (
          <>
            <div
              className="br-fame-panel-ef-row"
              data-modifier-sign={modifier >= 0 ? 'positive' : 'negative'}
            >
              <dt
                data-testid={`${testidPrefix}-disguise-label`}
              >
                Disguise: {DISGUISE_QR_LABEL[disguiseQr]}
              </dt>
              <dd
                className="br-mono"
                data-testid={`${testidPrefix}-modifier`}
              >
                {modifier > 0 ? `+${modifier}` : `${modifier}`}
              </dd>
            </div>
            <div className="br-fame-panel-ef-row br-fame-panel-ef-row-effective">
              <dt>Effective EF</dt>
              <dd
                className="br-mono"
                data-testid={`${testidPrefix}-effective-ef`}
              >
                {effectiveEf}
              </dd>
            </div>
          </>
        )}
      </dl>

      <div
        className="br-fame-panel-outcome"
        data-testid={`${testidPrefix}-outcome`}
        data-outcome-state={hasOutcome ? outcome : 'pending'}
      >
        {hasOutcome ? (
          <>
            <span className="br-fame-panel-outcome-cell">{outcome}</span>
            <span className="br-fame-panel-outcome-label">
              {OUTCOME_LABEL[outcome as FameOutcomeCell]}
            </span>
          </>
        ) : (
          <span className="br-fame-panel-outcome-pending">Awaiting Perception roll…</span>
        )}
      </div>

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-fame-panel-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
